import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { useTranslation } from 'react-i18next'
import LoadingAnimation from './LoadingAnimation.jsx'

const BOOK_ICON = (
  <svg width="12" height="12" viewBox="0 0 12 12"
    fill="none" stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 2h4a1 1 0 0 1 1 1v7a1 1 0 0 0-1-1H2V2z" />
    <path d="M10 2H6a1 1 0 0 0-1 1v7a1 1 0 0 1 1-1h4V2z" />
  </svg>
)

function ChevronIcon({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12"
      fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 300ms ease', flexShrink: 0 }}
      aria-hidden="true">
      <polyline points="2,4 6,8 10,4" />
    </svg>
  )
}

export default function SourceAttribution({ sources, answer, answer_html }) {
  const { t } = useTranslation()
  const isHistory = sources === undefined

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const hasBooks = Array.isArray(sources) && sources.length > 0
  const alreadyComplete = hasBooks && answer !== null
  const isCollapsible = hasBooks && sources.length >= 4
  const answerReady = answer !== null

  const [pillsLanded, setPillsLanded] = useState(reducedMotion || alreadyComplete || isHistory)
  const [expanded, setExpanded] = useState(false)

  // Schedule pillsLanded after stagger animation completes
  useEffect(() => {
    if (!hasBooks || pillsLanded) return
    if (reducedMotion) { setPillsLanded(true); return }
    const delay = isCollapsible
      ? 400
      : (sources.length - 1) * 400 + 350 + 50
    const timer = setTimeout(() => setPillsLanded(true), delay)
    return () => clearTimeout(timer)
  }, [sources]) // eslint-disable-line react-hooks/exhaustive-deps

  // History message: no source attribution, no animation
  if (isHistory) {
    return <div className="message-ai"><ReactMarkdown>{answer}</ReactMarkdown></div>
  }

  // No sources: either waiting (show spinner) or answer arrived without RAG (show answer directly)
  if (!hasBooks) {
    if (answerReady) return answer_html
      ? <div className="message-ai" dangerouslySetInnerHTML={{ __html: answer_html }} />
      : <div className="message-ai"><ReactMarkdown>{answer}</ReactMarkdown></div>
    return <LoadingAnimation />
  }

  // Sources arrived
  const pillElements = sources.map((src, i) => (
    <span
      key={src + i}
      className={reducedMotion || alreadyComplete ? 'source-pill' : 'source-pill source-pill--animate'}
      style={reducedMotion || alreadyComplete ? undefined : { animationDelay: `${i * 400}ms` }}
    >
      {BOOK_ICON}
      {src}
    </span>
  ))

  return (
    <div className="source-attribution">
      {/* Status row: shown only while answer is pending */}
      {!answerReady && (
        <div className="source-status">
          <span className="source-dot" />
          <span className="source-count">{t('source.found', { count: sources.length })}</span>
        </div>
      )}

      {/* Pills: collapsible (4+) or inline (1-3) */}
      {isCollapsible ? (
        <div>
          <button
            className="source-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {BOOK_ICON}
            {t('source.toggle', { count: sources.length })}
            <ChevronIcon open={expanded} />
          </button>
          <div
            className="source-pills-collapse"
            style={{ maxHeight: expanded ? '500px' : '0' }}
            aria-hidden={!expanded}
          >
            <div className="source-pills">{pillElements}</div>
          </div>
        </div>
      ) : (
        <div className="source-pills">{pillElements}</div>
      )}

      {/* Answer: mounts only after pills land, triggering fade-in animation */}
      {pillsLanded && answerReady && (
        answer_html
          ? <div className={reducedMotion ? 'message-ai' : 'message-ai source-answer-fade'} dangerouslySetInnerHTML={{ __html: answer_html }} />
          : <div className={reducedMotion ? 'message-ai' : 'message-ai source-answer-fade'}><ReactMarkdown>{answer}</ReactMarkdown></div>
      )}

      {/* Keep loading spinner while answer is pending */}
      {!answerReady && <LoadingAnimation />}
    </div>
  )
}
